import { tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDate, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useDeleteCommissionsSettingsRule } from "../../../../../common/api/queries";
import ActionTextIcon from "../../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../../common/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "../../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../../common/constants";
import { TopAgentType } from "../../../../../common/enums";
import { Agent } from "../../../../agent/types";
import { formatAgentName } from "../../../../agent/utils";
import { CommissionsSettingsInterval, CommissionsSettingsRule } from "../../types";
import { CommissionSourceTag } from "../CommissionSourceTag";
import { CommissionsSettingsRuleView } from "./CommissionsSettingsRuleView";

interface Props {
  rules: CommissionsSettingsRule[];
  agent?: Agent;
  showActions: boolean;
  onUpdateClick?: (rule: CommissionsSettingsRule) => void;
}

interface IntervalWithRule {
  interval: CommissionsSettingsInterval;
  rule: CommissionsSettingsRule;
  ruleIndex: number;
}

export const CommissionsSettingsRuleTableView = ({ rules, agent, showActions, onUpdateClick }: Props) => {
  const { t } = useTranslation();

  const isTopAgentTypePlus = agent?.topAgentType === TopAgentType.PLUS;
  const isTopAgent = agent?.id === agent?.accessTreePath;

  const intervalsWithRule = rules.flatMap((rule, ruleIndex) =>
    rule.intervals.map<IntervalWithRule>((interval) => ({ interval, rule, ruleIndex })),
  );

  const deleteCommissionsSettingsRule = useDeleteCommissionsSettingsRule();

  const handleOnCell = (record: IntervalWithRule, index?: number) => {
    if (!isDefined(index)) {
      return { rowSpan: 0 };
    }

    return index === 0 || record.rule.id !== intervalsWithRule[index - 1]?.rule.id
      ? { rowSpan: record.rule.intervals.length }
      : { rowSpan: 0 };
  };

  const getColumns = () => {
    const columns: ColumnsType<IntervalWithRule> = [
      {
        key: "rule",
        title: t("commissions.settings.sections.rule"),
        render: (_, record) => <CommissionsSettingsRuleView rule={record.rule} />,
        onCell: handleOnCell,
      },
    ];

    const intervalColumns: ColumnsType<IntervalWithRule> = [
      {
        key: "startDate",
        title: t("commissions.settings.attrs.settings.startDate"),
        width: 110,
        render: (_, record) => formatLocaleDate(record.interval.startDate),
      },
      {
        key: "endDate",
        title: t("commissions.settings.attrs.settings.endDate"),
        width: 110,
        render: (_, record) => (record.interval.endDate ? formatLocaleDate(record.interval.endDate) : undefined),
      },
    ];

    if (isTopAgentTypePlus && !isTopAgent) {
      intervalColumns.push({
        key: "commissionAmountPerPoint",
        title: t("commissions.settings.attrs.settings.commissionAmountPerPoint"),
        align: "right",
        width: 160,
        render: (_, record) =>
          isDefined(record.interval.commissionAmountPerPoint)
            ? formatLocaleCurrency(record.interval.commissionAmountPerPoint)
            : undefined,
      });
    } else {
      intervalColumns.push(
        {
          key: "commissionSource",
          title: t("commissions.settings.enums.commissionSource._label"),
          width: 115,
          render: (_, record) =>
            record.interval.source ? (
              <CommissionSourceTag source={record.interval.source} style={{ marginRight: 0 }} />
            ) : undefined,
        },
        {
          key: "superiorAgentId",
          title: t("commissions.settings.attrs.settings.superiorAgentId"),
          width: 105,
          ellipsis: { showTitle: false },
          render: (_, record) => <Ellipsis>{formatAgentName(record.interval.superiorAgent)}</Ellipsis>,
        },
        {
          key: "initialCommissionRate",
          title: t("commissions.settings.attrs.settings.initialCommissionRate"),
          align: "right",
          width: 115,
          render: (_, record) =>
            isDefined(record.interval.initialCommissionRate)
              ? formatLocalePercentageNumber(record.interval.initialCommissionRate)
              : undefined,
        },
        {
          key: "subsequentCommissionRate",
          title: t("commissions.settings.attrs.settings.subsequentCommissionRate"),
          align: "right",
          width: 110,
          render: (_, record) =>
            isDefined(record.interval.subsequentCommissionRate)
              ? formatLocalePercentageNumber(record.interval.subsequentCommissionRate)
              : undefined,
        },
      );
    }

    columns.push({
      key: "intervals",
      title: t("commissions.settings.sections.intervals"),
      children: intervalColumns,
    });

    if (showActions) {
      columns.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 180,
        render: (_, record) => (
          <>
            <ActionTextIcon
              icon="edit"
              color="blue"
              text={t("common.edit")}
              onClick={() => onUpdateClick?.(record.rule)}
            />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("commissions.settings.helpers.deleteRuleConfirm")}
              icon={<PopconfirmDeleteIcon />}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="danger"
              onConfirm={() =>
                agent ? deleteCommissionsSettingsRule.mutate({ id1: agent.id, id2: record.rule.id }) : undefined
              }
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </>
        ),
        onCell: handleOnCell,
      });
    }

    return columns;
  };

  return (
    <Table<IntervalWithRule>
      {...tableStandardProps()}
      rowKey={(record) => record.rule.id + record.interval.id}
      rowClassName={(record) => (record.ruleIndex % 2 === 0 ? "table-row-light" : "table-row-dark")}
      columns={getColumns()}
      scroll={{ x: TableSizes.LARGE }}
      dataSource={intervalsWithRule}
      pagination={false}
    />
  );
};
